/** @jsxImportSource @emotion/react */
import css from '@emotion/css/macro'
import React, { useState, useCallback, useEffect } from 'react'
import { Alert, Spin } from 'antd'
import BaseModal from '../ts-components/BaseModal'
import { SwitchInput } from '../ts-components/SwitchInput'
import Button from '../ts-components/Button'
import useFeatureFlags from '@sportninja/common/hooks/useFeatureFlags'
import { FeatureFlagName, FeatureFlags } from '@sportninja/common/types'

interface FeatureFlagsModalProps {
  isOpen: boolean
  onClose: () => void
  accountId?: string | null
}

interface FeatureFlagConfig {
  name: FeatureFlagName
  title: string
  description: string
}

const FEATURE_FLAGS_CONFIG: FeatureFlagConfig[] = [
  {
    name: 'ai-summary',
    title: 'AI Summary',
    description: 'Enable AI-powered game summaries and recaps',
  },
  {
    name: 'livestreaming',
    title: 'Livestreaming',
    description: 'Enable live video streaming capabilities',
  },
]

const FeatureFlagsModal: React.FC<FeatureFlagsModalProps> = ({
  isOpen,
  onClose,
  accountId,
}) => {
  const {
    flags,
    loading,
    error: hookError,
    updateFlag,
    isUpdating,
    refetch,
  } = useFeatureFlags(accountId)

  const [draftFlags, setDraftFlags] = useState<FeatureFlags | null>(null)
  const [saveError, setSaveError] = useState<string | null>(null)
  const [isSaving, setIsSaving] = useState(false)

  useEffect(() => {
    if (isOpen && flags) {
      setDraftFlags({ ...flags })
      setSaveError(null)
    }
  }, [isOpen, flags])

  useEffect(() => {
    if (isOpen && accountId) {
      refetch()
    }
  }, [isOpen, accountId, refetch])

  const handleToggle = useCallback((name: FeatureFlagName, value: boolean) => {
    setDraftFlags((prev) => {
      if (!prev) {
        return prev
      }
      return {
        ...prev,
        [name]: value,
      }
    })
    setSaveError(null)
  }, [])

  const handleSave = useCallback(async () => {
    if (!draftFlags || !flags) {
      return
    }

    setIsSaving(true)
    setSaveError(null)

    try {
      const updatePromises = FEATURE_FLAGS_CONFIG.map(async (config) => {
        const draftValue = draftFlags[config.name]
        const currentValue = flags[config.name]

        if (draftValue !== currentValue) {
          await updateFlag(config.name, draftValue)
        }
      })

      await Promise.all(updatePromises)
      onClose()
    } catch (error: unknown) {
      const errorMessage =
        error instanceof Error ? error.message : 'Failed to save feature flags'
      setSaveError(errorMessage)
    } finally {
      setIsSaving(false)
    }
  }, [draftFlags, flags, updateFlag, onClose])

  const handleCancel = useCallback(() => {
    onClose()
  }, [onClose])

  const renderFooter = useCallback(() => {
    return (
      <div
        css={css`
          display: flex;
          justify-content: flex-end;
          gap: 16px;
          width: 100%;
        `}
      >
        <Button
          text='CANCEL'
          onClick={handleCancel}
          variant='secondary'
          disabled={isSaving || isUpdating}
        />
        <Button
          text={isSaving || isUpdating ? 'SAVING...' : 'SAVE'}
          onClick={handleSave}
          variant='primary'
          disabled={isSaving || isUpdating || !draftFlags}
        />
      </div>
    )
  }, [handleCancel, handleSave, isSaving, isUpdating, draftFlags])

  const renderFlagRow = useCallback(
    (config: FeatureFlagConfig) => {
      if (!draftFlags) {
        return null
      }

      const value = draftFlags[config.name] ?? true

      return (
        <div
          key={config.name}
          css={css`
            display: flex;
            align-items: center;
            gap: 16px;
          `}
        >
          <SwitchInput
            title={config.title}
            subtitle={config.description}
            value={value}
            onChange={(checked) => handleToggle(config.name, checked)}
            disabled={isSaving || isUpdating}
          />
        </div>
      )
    },
    [draftFlags, handleToggle, isSaving, isUpdating]
  )

  return (
    <BaseModal
      isOpen={isOpen}
      onClose={handleCancel}
      title='Feature Flags'
      desktopWidth='500px'
      mobileWidth='90vw'
      actions={renderFooter()}
    >
      {(hookError || saveError) && (
        <Alert
          message={hookError?.message || saveError}
          type='error'
          showIcon
          closable
          onClose={() => setSaveError(null)}
          css={css`
            margin-bottom: 16px;
          `}
        />
      )}

      {loading ? (
        <div
          css={css`
            display: flex;
            justify-content: center;
            align-items: center;
            padding: 40px;
          `}
        >
          <Spin size='large' />
        </div>
      ) : (
        <div
          css={css`
            display: flex;
            flex-direction: column;
            gap: 16px;
          `}
        >
          {FEATURE_FLAGS_CONFIG.map((config) => renderFlagRow(config))}
        </div>
      )}
    </BaseModal>
  )
}

export default FeatureFlagsModal
